import React, { useState, useEffect, useCallback } from 'react'
import { connect, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import {
  PlaylistSongTable,
  PopupProvider,
  ListButton,
  PlayAllButton,
  RoundCornerButton,
  PlaylistHeader,
  DialogModal
} from 'components'
import Tooltip from 'components/Tooltip'
import { Button } from '@dsny/dsny-component-library'
import { MainLayout } from 'containers'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { PlayListService } from 'services'
import { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList'
import ModalActions from 'reducers/Modal'
import { thunks as DownloadThunks } from 'reducers/Downloads'
import { AnchorPositions, PopupDirections } from 'constants/enums'
import {
  PencilEditIcon,
  SettingsIcon,
  playlistBanner1,
  playlistBanner2,
  playlistBanner3,
  playlistBanner4,
  playlistBanner5,
  playlistBanner6,
  playlistBanner7,
  playlistBanner8,
  playlistBanner9,
  playlistBanner10,
  playlistBanner11,
  playlistBanner12,
  playlistBanner13,
  playlistBanner14,
  playlistBanner15,
  playlistBanner16,
  playlistBanner17
} from 'images'
import { getStringFromSeconds } from 'utils/dateHelpers'
import { browserHelpers } from 'utils'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import { useDesktopMediaQuery } from 'utils/hooks/useMediaQueries'
import { theme } from '../../../tailwind.theme'
import pdStyles from './PlaylistDetail.module.scss'
import PlaylistDetailWithRollout from './PlaylistDetailWithRollout'

export const PlaylistDetailContent = ({
  isIpadView,
  isPlaying,
  playlist,
  selectedPlaylist
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const isDesktopV2 = useDesktopMediaQuery()
  const navigate = useNavigate()
  const { id } = useParams()
  const [showClearModal, setShowClearModal] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [banner, setBanner] = useState(null)
  // Redux remains the rollback source until the App shell phase retires it.
  const showCompactActions =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
      ? !isDesktopV2
      : isIpadView

  useEffect(() => {
    const banners = [
      playlistBanner1,
      playlistBanner2,
      playlistBanner3,
      playlistBanner4,
      playlistBanner5,
      playlistBanner6,
      playlistBanner7,
      playlistBanner8,
      playlistBanner9,
      playlistBanner10,
      playlistBanner11,
      playlistBanner12,
      playlistBanner13,
      playlistBanner14,
      playlistBanner15,
      playlistBanner16,
      playlistBanner17
    ]
    setBanner(banners[Math.floor(Math.random() * banners.length)])
    loadData(id)
  }, [id])

  const loadData = useCallback(
    (id) => {
      dispatch(
        PlaylistThunks.refreshPlaylist(id, (playlist) => {
          dispatch(PlaylistActions.setPlaylistUnderDisplay(playlist))
        })
      )
    },
    [dispatch]
  )

  const handleEditClick = useCallback(() => {
    dispatch(PlaylistActions.setPlaylistUnderEditing(playlist))
    dispatch(ModalActions.togglePlaylistModal())
  }, [playlist])

  const handlePlayClick = useCallback(() => {
    const songs = (playlist && playlist.songs) || []
    if (songs && playlist) {
      dispatch(
        PlayQueueThunks.playPlaylist(
          {
            ...playlist,
            songs
          },
          {
            source: PLAY_SOURCES.PLAYLIST_DETAIL,
            trigger: PLAY_TRIGGERS.PLAY_ALL
          }
        )
      )
    }
  }, [playlist])

  const handleRemoveList = useCallback(() => {
    dispatch(
      PlaylistThunks.deletePlaylist(playlist, () => navigate('/playlists'))
    )
  }, [playlist, navigate])

  const handleClearList = useCallback(() => {
    dispatch(PlaylistThunks.deleteAllSongs(playlist))
  }, [playlist])

  const handleDownloadPlaylist = useCallback(
    async (format) => {
      const { songs } = playlist
      let downloadableSongs = songs.filter((song) => song.candownload === true)

      if (downloadableSongs.length > 0) {
        dispatch(
          DownloadThunks.addPlaylistToDownloadQueue(
            playlist,
            format,
            'playlist_detail'
          )
        )
      }
    },
    [playlist]
  )

  const getTotalTime = useCallback(() => {
    const songs = (playlist && playlist.songs) || []
    let result = 0
    songs.forEach((song) => {
      if (song.duration) {
        result += song.duration
      }
    })
    return getStringFromSeconds(result)
  }, [playlist])

  const isPlayingCurrentPlaylist = useCallback(() => {
    return (
      isPlaying &&
      (selectedPlaylist && selectedPlaylist.playlistid) === playlist.playlistid
    )
  }, [isPlaying, selectedPlaylist, playlist])

  const renderEditButton = () => (
    <button
      type="button"
      className="focus:outline-none"
      onClick={handleEditClick}
    >
      <PencilEditIcon />
    </button>
  )

  const renderButtons = () => {
    const { songs } = playlist
    let downloadableSongs = songs.filter((song) => song.candownload === true)

    return (
      <div className="flex flex-row items-start justify-between absolute top-0 right-0 mt-10 mr-8">
        <RoundCornerButton
          text={t('playlist.clear-playlist')}
          className="mr-4 px-4 py-1 bg-gray-770"
          textClassName="text-white text-sm"
          onClick={() => setShowClearModal(true)}
        />
        <a
          href={PlayListService.generatePdfUrl(playlist.playlistid)}
          className="mr-4 px-4 py-1 bg-cerulean text-white text-sm rounded-full"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('playlist.pdf')}
        </a>
        <div className="mr-4">
          <Tooltip
            hasArrow={false}
            placement="top"
            text={t('release-info.download-all-wav')}
            containerStyles={{ width: 276, whiteSpace: 'normal' }}
            theme={theme}
          >
            <Button
              type="button"
              className={`w-full btn shadow-md self-center ${pdStyles['submit-btn']}`}
              setting="player"
              variant="primary"
              onClick={() => handleDownloadPlaylist('wav')}
              disabled={downloadableSongs.length === 0}
            >
              <div className="flex flex-row justify-between">
                <h1>{t('general.download-all-wav')}</h1>
              </div>
            </Button>
          </Tooltip>
        </div>
        <div className="mr-4">
          <Tooltip
            hasArrow={false}
            placement="top"
            text={t('release-info.download-all-mp3')}
            containerStyles={{ width: 276, whiteSpace: 'normal' }}
            theme={theme}
          >
            <Button
              type="button"
              className={`w-full btn shadow-md self-center ${pdStyles['submit-btn']}`}
              setting="player"
              variant="secondary"
              onClick={() => handleDownloadPlaylist('mp3')}
              disabled={downloadableSongs.length === 0}
            >
              <div className="flex flex-row justify-between">
                <h1>{t('general.download-all-mp3')}</h1>
              </div>
            </Button>
          </Tooltip>
          <div className={pdStyles['download-button-text']}>
            {`${downloadableSongs.length}/${songs.length} available for download`}
          </div>
        </div>
      </div>
    )
  }

  const renderRemoveButton = () => (
    <button
      type="button"
      className="focus:outline-none text-white underline text-sm absolute right-0 bottom-0 mr-8 mb-6"
      onClick={() => setShowRemoveModal(true)}
    >
      {t('playlist.remove-playlist')}
    </button>
  )

  const renderDropDownOptions = () => {
    const { songs } = playlist
    return (
      <>
        {browserHelpers.browserAllowsDownload && songs.length > 0 && (
          <ListButton
            name={t('playlist.download-playlist')}
            onClick={handleDownloadPlaylist}
          />
        )}
        <ListButton
          name={t('playlist.clear-playlist-full')}
          onClick={() => setShowClearModal(true)}
        />
        {browserHelpers.browserAllowsDownload && (
          <a
            href={PlayListService.generatePdfUrl(playlist.playlistid)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListButton name={t('playlist.download-pdf')} />
          </a>
        )}
        <ListButton
          name={t('playlist.remove-playlist')}
          onClick={() => setShowRemoveModal(true)}
        />
      </>
    )
  }

  const renderSettingsButton = () => (
    <PopupProvider
      tapOutSideToDismiss
      anchorPosition={AnchorPositions.bottomLeft}
      popupDirection={PopupDirections.downLeft}
      renderTrigger={({ setOnOff }) => (
        <button
          type="button"
          className="focus:outline-none z-0 my-5 mr-8 text-white"
          onClick={(event) => {
            event.stopPropagation()
            setOnOff(true)
          }}
        >
          <SettingsIcon />
        </button>
      )}
    >
      <div className="bg-white rounded text-black overflow-y-auto max-h-64 scrollbar-blue w-40">
        {renderDropDownOptions()}
      </div>
    </PopupProvider>
  )

  const renderPanel = (playlist) => {
    const { name, desc, songs } = playlist

    return (
      <div className="flex flex-row mt-6 mx-8 relative justify-between items-start">
        <img
          src={banner}
          className="absolute top-0 left-0 w-full h-full object-cover z-0"
          alt="coverart"
        />
        <div className="absolute top-0 left-0 w-full h-full z-0" />
        <div className="flex flex-col w-88 z-0 ml-3 mb-4 mt-2">
          <div className="flex flex-row justify-between w-full">
            <div className="text-white text-xl mr-4">{name}</div>
            {renderEditButton()}
          </div>
          <div className="text-white text-sm min-h-24 my-2">{desc}</div>
          <div className="flex flex-row justify-between w-full items-center">
            <PlayAllButton
              disabled={songs.length === 0}
              isPlaying={isPlayingCurrentPlaylist()}
              onClick={handlePlayClick}
            />
            <div className="text-white text-sm">{`${t(
              'playlist.total-time'
            )} ${getTotalTime()}`}</div>
          </div>
        </div>
        {!showCompactActions && renderButtons()}
        {!showCompactActions && renderRemoveButton()}
        {showCompactActions && renderSettingsButton()}
      </div>
    )
  }

  const renderClearModal = () => (
    <DialogModal
      title={t('playlist.clear-playlist-title')}
      onClickYes={() => {
        handleClearList()
        setShowClearModal(false)
      }}
      onClickCancel={() => setShowClearModal(false)}
    >
      <div className="w-full flex flex-col items-center">
        <div className="mt-8 mb-6 mx-8 text-center text-xs">
          {t('playlist.clear-playlist-message')}
        </div>
      </div>
    </DialogModal>
  )

  const renderRemoveModal = () => (
    <DialogModal
      title={t('playlist.remove-playlist-title')}
      onClickYes={() => {
        handleRemoveList()
        setShowRemoveModal(false)
      }}
      onClickCancel={() => setShowRemoveModal(false)}
    >
      <div className="w-full flex flex-col items-center">
        <div className="mt-8 mb-6 mx-8 text-center text-xs">
          {t('playlist.remove-playlist-message').replace('{0}', playlist.name)}
        </div>
      </div>
    </DialogModal>
  )

  const songs = (playlist && playlist.songs) || []
  return (
    <div
      className="flex flex-col flex-1 overflow-hidden"
      data-responsive-breakpoints={responsiveBreakpointsVersion}
      data-responsive-breakpoints-page={
        RESPONSIVE_BREAKPOINT_PAGES.PLAYLIST_DETAIL
      }>
      {playlist && renderPanel(playlist)}
      <PlaylistSongTable
        isIpadView={isIpadView}
        songs={songs}
        playlist={playlist} />
      {showClearModal && renderClearModal()}
      {showRemoveModal && renderRemoveModal()}
    </div>
  )
}

const playlistDetailPropTypes = {
  isIpadView: PropTypes.bool,
  isPlaying: PropTypes.bool,
  playlist: PropTypes.object,
  selectedPlaylist: PropTypes.object
}

PlaylistDetailContent.propTypes = playlistDetailPropTypes

const mapStateToProps = ({ global, playList, playQueue, player }) => ({
  isIpadView: global.isIpadView,
  playlist: playList.playlistUnderDisplay,
  selectedPlaylist: playQueue.selectedItem,
  isPlaying: player.isPlaying
})

const ConnectedPlaylistDetailContent = connect(mapStateToProps)(
  PlaylistDetailContent
)

const PlaylistDetail = () => (
  <MainLayout
    menu={<PlaylistHeader />}
    content={
      <PlaylistDetailWithRollout>
        <ConnectedPlaylistDetailContent />
      </PlaylistDetailWithRollout>
    } />
)

export default PlaylistDetail
